import { useAgentComplianceReviewDocumentQuery } from '@client/graphql/__generated__/main-operations';
import { AgentComplianceReviewDocumentQuery } from '@client/graphql/__generated__/types';

type AgentComplianceReviewDocument =
  AgentComplianceReviewDocumentQuery['agentComplianceReviewDocument'];

interface UseAgentComplianceReviewDocumentArgs {
  token: string | undefined;
  ingestionFolderUuid: string | undefined;
  ingestionDocumentId: string | undefined;
}

interface UseAgentComplianceReviewDocumentResult {
  document: AgentComplianceReviewDocument | undefined;
  loading: boolean;
  error: ReturnType<typeof useAgentComplianceReviewDocumentQuery>['error'];
}

/**
 * Loads the detail (PDF url, fields, suggestions, successful checks) for a
 * single selected document in the agent compliance review.
 *
 * Skips until a document is selected and the token/folder are known. Uses a
 * `cache-first` policy so revisiting an already-viewed document is instant, and
 * opts out of the global error toast (`skipGlobalErrorHandler`) so callers can
 * render inline error state instead.
 */
export const useAgentComplianceReviewDocument = ({
  token,
  ingestionFolderUuid,
  ingestionDocumentId,
}: UseAgentComplianceReviewDocumentArgs): UseAgentComplianceReviewDocumentResult => {
  const { data, loading, error } = useAgentComplianceReviewDocumentQuery({
    variables: {
      input: {
        token: token ?? '',
        ingestionFolderUuid: ingestionFolderUuid ?? '',
        ingestionDocumentId: ingestionDocumentId ?? '',
      },
    },
    skip: !ingestionDocumentId || !token || !ingestionFolderUuid,
    fetchPolicy: 'cache-first',
    context: { skipGlobalErrorHandler: true },
  });

  return {
    document: data?.agentComplianceReviewDocument,
    loading,
    error,
  };
};
